import { useState, useCallback, useRef, type CSSProperties } from 'react';
import type { Board } from './useGameBoard';

export interface FloatingScore {
  id: number;
  text: string;
  /** random offset -12..+12 px so rapid entries don't fully overlap */
  offset: number;
  /** board row (0-8) — used to position the label over the correct cell */
  row: number;
  /** board column (0-8) */
  col: number;
}

export interface UseGameEffectsReturn {
  isEffectsEnabled: boolean;
  toggleEffects: () => void;
  isSoundEnabled: boolean;
  toggleSound: () => void;
  getCellEffectClass: (r: number, c: number) => string;
  getCellEffectStyle: (r: number, c: number) => CSSProperties | undefined;
  triggerCorrect: (r: number, c: number, updatedBoard: Board, value?: number) => void;
  triggerWrong: (r: number, c: number) => void;
  triggerComplete: (r: number, c: number) => void;
  playCorrect: () => void;
  playWrong: () => void;
  playComplete: () => void;
  playHint: () => void;
  vibrateCorrect: () => void;
  vibrateWrong: () => void;
  vibrateComplete: () => void;
  /** Floating "+10" popups — render these over the board */
  floatingScores: FloatingScore[];
  /** True briefly after a correct placement — add CSS pulse class to score chip */
  scorePulsing: boolean;
  /** True for ~3.5 s after board completion — trigger confetti */
  showConfetti: boolean;
}

export function useGameEffects(): UseGameEffectsReturn {
  const [isEffectsEnabled, setIsEffectsEnabled] = useState(
    () => localStorage.getItem('sudoku_effects') !== 'false',
  );
  const [isSoundEnabled, setIsSoundEnabled] = useState(
    () => localStorage.getItem('sudoku_sound') !== 'false',
  );
  // key: "r,c" → CSS class (pop / headshake / shimmer)
  const [animatingCells, setAnimatingCells] = useState<Map<string, string>>(new Map());
  // key: "r,c" present → green flash running
  const [flashingCells, setFlashingCells] = useState<Set<string>>(new Set());
  // ripple delay map
  const [rippleCells, setRippleCells] = useState<Map<string, number>>(new Map());
  // floating "+10" entries
  const [floatingScores, setFloatingScores] = useState<FloatingScore[]>([]);
  const floatIdRef = useRef(0);
  // score chip pulse
  const [scorePulsing, setScorePulsing] = useState(false);
  // confetti trigger
  const [showConfetti, setShowConfetti] = useState(false);

  const audioCtxRef = useRef<AudioContext | null>(null);

  const toggleEffects = useCallback(() => {
    setIsEffectsEnabled(prev => {
      const next = !prev;
      localStorage.setItem('sudoku_effects', String(next));
      return next;
    });
  }, []);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('sudoku_sound', String(next));
      return next;
    });
  }, []);

  const getAudioCtx = (): AudioContext => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    return audioCtxRef.current;
  };

  // ── Ses: Doğru — sıcak marimba/kalimba toku ──
  const playCorrect = useCallback(() => {
    if (!isSoundEnabled) return;
    try {
      const ctx = getAudioCtx();
      const t = ctx.currentTime;

      // 1. "Tok" transient — alçak frekanslı filtrelenmiş gürültü patlaması (organik ahşap vuruşu)
      const bufSize = Math.floor(ctx.sampleRate * 0.018);
      const noiseBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const nd = noiseBuf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) nd[i] = Math.random() * 2 - 1;
      const noiseSrc = ctx.createBufferSource();
      noiseSrc.buffer = noiseBuf;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.value = 380;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.08, t);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.018);
      noiseSrc.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noiseSrc.start(t);
      noiseSrc.stop(t + 0.02);

      // 2. Sıcak ana ton — triangle C5 (523 Hz), doğal marimba çürümesi
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'triangle';
      osc1.frequency.value = 523.25;
      gain1.gain.setValueAtTime(0, t);
      gain1.gain.linearRampToValueAtTime(0.22, t + 0.008);
      gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.34);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(t);
      osc1.stop(t + 0.35);

      // 3. Yumuşak harmonik — triangle G5 (784 Hz), mükemmel 5li, çok daha sessiz ve kısa
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'triangle';
      osc2.frequency.value = 783.99;
      gain2.gain.setValueAtTime(0, t);
      gain2.gain.linearRampToValueAtTime(0.09, t + 0.008);
      gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(t);
      osc2.stop(t + 0.19);
    } catch { /* AudioContext izni yok ya da desteklenmiyor */ }
  }, [isSoundEnabled]);

  // ── Ses: Hatalı (sert çift tok-tok + ince dissonans) ──
  const playWrong = useCallback(() => {
    if (!isSoundEnabled) return;
    try {
      const ctx = getAudioCtx();
      [0, 0.10].forEach((delay, i) => {
        const t = ctx.currentTime + delay;
        // Ana tok — sine 130 Hz, sert atak
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 130;
        gain.gain.setValueAtTime(0.28, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.055);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.06);
        // Dissonant katman — 174 Hz (biraz tiz), çok sessiz
        if (i === 0) {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.type = 'sawtooth';
          osc2.frequency.value = 174;
          gain2.gain.setValueAtTime(0.07, t);
          gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.start(t);
          osc2.stop(t + 0.05);
        }
      });
    } catch { }
  }, [isSoundEnabled]);

  // ── Ses: Tamamlandı (C5→E5→G5→C6 arpej) ──
  const playComplete = useCallback(() => {
    if (!isSoundEnabled) return;
    try {
      new Audio('/sounds/complete.wav').play().catch(() => {});
    } catch { }
  }, [isSoundEnabled]);

  // ── Ses: İpucu — yumuşak A5 çan ──
  const playHint = useCallback(() => {
    if (!isSoundEnabled) return;
    try {
      const ctx = getAudioCtx();
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880; // A5
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.14, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.3);
    } catch { }
  }, [isSoundEnabled]);

  // ── Ses: Satır/Sütun tamamlandı ──
  const playLineComplete = useCallback(() => {
    if (!isSoundEnabled) return;
    try {
      const a = new Audio('/sounds/sequence_complete.mp3');
      a.currentTime = 0.8;
      a.play().catch(() => {});
      setTimeout(() => {
        let v = 1; const fade = setInterval(() => {
          v = Math.max(0, v - 0.1); a.volume = v;
          if (v <= 0) { clearInterval(fade); a.pause(); }
        }, 30);
      }, 1300);
    } catch { }
  }, [isSoundEnabled]);

  // ── Ses: Kutu tamamlandı ──
  const playBoxComplete = useCallback(() => {
    if (!isSoundEnabled) return;
    try {
      const a = new Audio('/sounds/sequence_complete.mp3');
      a.currentTime = 0.8;
      a.play().catch(() => {});
      setTimeout(() => {
        let v = 1; const fade = setInterval(() => {
          v = Math.max(0, v - 0.1); a.volume = v;
          if (v <= 0) { clearInterval(fade); a.pause(); }
        }, 30);
      }, 1300);
    } catch { }
  }, [isSoundEnabled]);

  // ── Titreşim ──
  const vibrateCorrect = useCallback(() => {
    if (!isSoundEnabled || !navigator.vibrate) return;
    navigator.vibrate(30);
  }, [isSoundEnabled]);

  const vibrateWrong = useCallback(() => {
    if (!isSoundEnabled || !navigator.vibrate) return;
    navigator.vibrate([20, 50, 20]);
  }, [isSoundEnabled]);

  const vibrateComplete = useCallback(() => {
    if (!isSoundEnabled || !navigator.vibrate) return;
    navigator.vibrate([100, 50, 100, 50, 200]);
  }, [isSoundEnabled]);

  // ── Animasyon yardımcısı: belirli süre sonra sınıfı kaldır ──
  const scheduleAnimation = (key: string, cls: string, durationMs: number) => {
    setAnimatingCells(prev => new Map(prev).set(key, cls));
    setTimeout(() => {
      setAnimatingCells(prev => {
        const next = new Map(prev);
        if (next.get(key) === cls) next.delete(key);
        return next;
      });
    }, durationMs);
  };

  // ── Yeşil flash: 420ms (mobil ile eşit) ──
  const scheduleFlash = (key: string) => {
    setFlashingCells(prev => new Set(prev).add(key));
    setTimeout(() => {
      setFlashingCells(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, 420);
  };

  // ── Uçan puan: +10 ilgili hücrenin üzerinden yükselip kaybolur ──
  const addFloatingScore = (text: string, row: number, col: number) => {
    const id = ++floatIdRef.current;
    const offset = Math.round(Math.random() * 16) - 8; // -8..+8 px
    setFloatingScores(prev => [...prev, { id, text, offset, row, col }]);
    setTimeout(() => {
      setFloatingScores(prev => prev.filter(s => s.id !== id));
    }, 1500);
  };

  // ── Doğru giriş: pop + yeşil flash + floating score + score pulse + tamamlama animasyonları ──
  const triggerCorrect = useCallback(
    (r: number, c: number, updatedBoard: Board, value?: number) => {
      if (!isEffectsEnabled && !isSoundEnabled) return;

      const key = `${r},${c}`;
      if (isEffectsEnabled) {
        scheduleAnimation(key, 'cell-pop', 300);
        scheduleFlash(key);
        addFloatingScore('+10', r, c);
        setScorePulsing(true);
        setTimeout(() => setScorePulsing(false), 500);

        // ── Aynı rakamlı diğer hücreler: hafif digit_pulse (mobil ile eşit) ──
        if (value) {
          for (let ri = 0; ri < 9; ri++) {
            for (let ci = 0; ci < 9; ci++) {
              if (ri === r && ci === c) continue;
              if (updatedBoard[ri]?.[ci] === value) {
                scheduleAnimation(`${ri},${ci}`, 'cell-digit-pulse', 600);
              }
            }
          }
        }
      }

      const DELAY = 80; // pop animasyonunun bitmesini bekle
      const rowDone = updatedBoard[r]?.every(v => v != null && v !== 0) ?? false;
      const colDone = updatedBoard.every(row => row[c] != null && row[c] !== 0);
      const br = Math.floor(r / 3) * 3;
      const bc = Math.floor(c / 3) * 3;
      const boxDone = Array.from({ length: 3 }, (_, i) =>
        Array.from({ length: 3 }, (_, j) => updatedBoard[br + i]?.[bc + j]),
      ).flat().every(v => v != null && v !== 0);

      // ── Satır: tüm hücreler eş zamanlı altın flash ──
      if (rowDone) {
        setTimeout(() => {
          if (isEffectsEnabled)
            for (let ci = 0; ci < 9; ci++) scheduleAnimation(`${r},${ci}`, 'cell-line-complete', 400);
          playLineComplete();
        }, DELAY);
      }

      // ── Sütun: tüm hücreler eş zamanlı altın flash ──
      if (colDone) {
        setTimeout(() => {
          if (isEffectsEnabled)
            for (let ri = 0; ri < 9; ri++) scheduleAnimation(`${ri},${c}`, 'cell-line-complete', 400);
          if (!rowDone) playLineComplete();
        }, DELAY);
      }

      // ── Kutu: tüm hücreler eş zamanlı violet flash ──
      if (boxDone) {
        setTimeout(() => {
          if (isEffectsEnabled)
            for (let i = 0; i < 3; i++)
              for (let j = 0; j < 3; j++)
                scheduleAnimation(`${br + i},${bc + j}`, 'cell-box-complete', 420);
          playBoxComplete();
        }, DELAY);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEffectsEnabled, isSoundEnabled, playLineComplete, playBoxComplete],
  );

  // ── Hatalı giriş: headshake ──
  const triggerWrong = useCallback(
    (r: number, c: number) => {
      if (!isEffectsEnabled) return;
      scheduleAnimation(`${r},${c}`, 'cell-headshake', 400);
    },
    [isEffectsEnabled],
  );

  // ── Tahta tamamlandı: ripple + konfeti ──
  const triggerComplete = useCallback(
    (r: number, c: number) => {
      if (!isEffectsEnabled) return;
      const newRipple = new Map<string, number>();
      for (let ri = 0; ri < 9; ri++)
        for (let ci = 0; ci < 9; ci++)
          newRipple.set(`${ri},${ci}`, (Math.abs(ri - r) + Math.abs(ci - c)) * 40);
      setRippleCells(newRipple);
      setTimeout(() => setRippleCells(new Map()), 1600);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3500);
    },
    [isEffectsEnabled],
  );

  // ── CSS sınıfı: tüm aktif efekt sınıflarını birleştir ──
  const getCellEffectClass = useCallback(
    (r: number, c: number): string => {
      const key = `${r},${c}`;
      const classes: string[] = [];
      const anim = animatingCells.get(key);
      if (anim) classes.push(anim);
      if (flashingCells.has(key)) classes.push('cell-correct-flash');
      if (rippleCells.has(key)) classes.push('cell-ripple');
      return classes.join(' ');
    },
    [animatingCells, flashingCells, rippleCells],
  );

  // ── CSS style: ripple gecikme değişkeni ──
  const getCellEffectStyle = useCallback(
    (r: number, c: number): CSSProperties | undefined => {
      const delay = rippleCells.get(`${r},${c}`);
      if (delay !== undefined) return { '--ripple-delay': `${delay}ms` } as CSSProperties;
      return undefined;
    },
    [rippleCells],
  );

  return {
    isEffectsEnabled,
    toggleEffects,
    isSoundEnabled,
    toggleSound,
    getCellEffectClass,
    getCellEffectStyle,
    triggerCorrect,
    triggerWrong,
    triggerComplete,
    playCorrect,
    playWrong,
    playComplete,
    playHint,
    vibrateCorrect,
    vibrateWrong,
    vibrateComplete,
    floatingScores,
    scorePulsing,
    showConfetti,
  };
}
